import { createContext, useContext } from "react";

import type { components } from "@/core/api/schema";

/**
 * The session's shape and context, kept apart from the provider on purpose: this file has no other imports,
 * so a dev-server hot reload of the provider's dependencies never creates a second context (which made
 * useSession throw until a full reload).
 */
export type Me = components["schemas"]["Me"];

/** `unavailable`: the API couldn't be reached to check the session (not the same as signed out). */
export type SessionStatus = "loading" | "signed-out" | "signed-in" | "unavailable";

export interface SessionState {
  status: SessionStatus;
  me: Me | null;
  /** Checks the session again (after `unavailable`). */
  retry(): void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  signInWithGoogle(): Promise<void>;
  signInWithEmail(email: string, password: string): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  /** Ends this portal's session in this browser; `everywhere` also ends every other session of the person. */
  signOut(options?: { everywhere?: boolean }): Promise<void>;
}

export const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}
